import React, { useState } from 'react';
import { MessageCircle, X, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useSound } from '../hooks/useSound';

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      text: "Hi! I'm Adil's AI assistant. How can I help you today?",
      sender: 'bot',
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const { playClickSound } = useSound();

  const quickReplies = [
    "What services do you offer?",
    "How much do you charge?",
    "Can I see your portfolio?",
    "How do I contact you?"
  ];

  const botResponses = {
    "what services": "I specialize in AI video creation including Health & Supplement Videos, ASMR Videos, Viral AI Baby Videos, AI Objects Talking Videos, Horror Story Reels, Skeleton Videos, Motivational Character Videos, and Realistic UGC & Ad Videos. Check out my Services section above!",
    "how much": "My hourly rate is $15/hour. Pricing varies by project complexity. I'm a Level 2 Fiverr Seller with 4+ years of experience and a perfect 5.0 rating!",
    "portfolio": "I have 7 amazing AI video samples showcasing my work across different categories. Scroll up to the Portfolio section to watch them!",
    "contact": "You can reach me via:\n📧 Email: fiverr1845@gmail.com\n💬 WhatsApp: +923174708214\n🌐 Fiverr: View my gigs\n📱 Facebook & Instagram (links in Contact section)\n\nI typically respond within 1 hour!"
  };

  const handleSend = () => {
    if (!inputMessage.trim()) return;

    playClickSound();
    
    // Add user message
    const userMsg = {
      text: inputMessage,
      sender: 'user',
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
    
    setMessages([...messages, userMsg]);
    
    // Generate bot response
    setTimeout(() => {
      const lowerInput = inputMessage.toLowerCase();
      let response = "Thanks for your message! For specific inquiries, please use the Contact form above or reach out via WhatsApp/Email. I'll respond within 1 hour!";
      
      for (const [key, value] of Object.entries(botResponses)) {
        if (lowerInput.includes(key)) {
          response = value;
          break;
        }
      }
      
      const botMsg = {
        text: response,
        sender: 'bot',
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };
      
      setMessages(prev => [...prev, botMsg]);
    }, 1000);
    
    setInputMessage('');
  };

  const handleQuickReply = (reply) => {
    playClickSound();
    setInputMessage(reply);
  };

  return (
    <>
      {/* Chat Toggle Button */}
      <button
        onClick={() => {
          playClickSound();
          setIsOpen(!isOpen);
        }}
        className={`fixed bottom-6 right-6 z-50 w-16 h-16 rounded-full bg-gradient-to-r from-emerald-600 to-emerald-700 text-white shadow-2xl shadow-emerald-500/50 flex items-center justify-center hover:scale-110 active:scale-95 transition-all duration-300 ${
          isOpen ? 'rotate-90' : 'animate-bounce-gentle'
        }`}
      >
        {isOpen ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6 animate-pulse" />}
      </button>

      {/* Chatbot Window */}
      {isOpen && (
        <div className="fixed bottom-24 right-6 z-50 w-96 h-[500px] bg-slate-900 border-2 border-emerald-500/30 rounded-2xl shadow-2xl shadow-emerald-500/20 flex flex-col animate-scale-in backdrop-blur-xl">
          {/* Header */}
          <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 p-4 rounded-t-2xl flex items-center gap-3">
            <div className="relative">
              <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-white/30">
                <img
                  src="/profile-photo.jpg"
                  alt="Adil"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = 'https://images.unsplash.com/photo-1633332755192-727a05c4013d?w=100&h=100&fit=crop';
                  }}
                />
              </div>
              <div className="absolute -bottom-0 -right-0 w-3 h-3 bg-green-400 rounded-full border-2 border-emerald-700 animate-pulse" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-white">Adil - AI Assistant</h3>
              <p className="text-xs text-emerald-100">Online • Responds in ~1hr</p>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'} animate-slide-in`}
              >
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                    msg.sender === 'user'
                      ? 'bg-gradient-to-r from-emerald-600 to-emerald-700 text-white rounded-tr-none'
                      : 'bg-slate-800 text-slate-200 rounded-tl-none border border-slate-700'
                  }`}
                >
                  <p className="text-sm whitespace-pre-line">{msg.text}</p>
                  <p className={`text-xs mt-1 ${msg.sender === 'user' ? 'text-emerald-100' : 'text-slate-500'}`}>
                    {msg.time}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Quick Replies */}
          {messages.length <= 2 && (
            <div className="px-4 pb-2">
              <p className="text-xs text-slate-400 mb-2">Quick questions:</p>
              <div className="flex flex-wrap gap-2">
                {quickReplies.map((reply, index) => (
                  <button
                    key={index}
                    onClick={() => handleQuickReply(reply)}
                    className="px-3 py-1 text-xs bg-slate-800 border border-slate-700 text-emerald-400 rounded-full hover:bg-slate-700 hover:border-emerald-500/50 transition-all duration-300 hover:scale-105"
                  >
                    {reply}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <div className="p-4 border-t border-slate-800">
            <div className="flex gap-2">
              <Input
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                placeholder="Type your message..."
                className="flex-1 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-emerald-500"
              />
              <Button
                onClick={handleSend}
                className="bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-500 hover:to-emerald-600 px-4"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Chatbot;
